// src/pages/Dashboard.tsx
import React, { useState, useEffect } from 'react';
import { 
  Monitor, 
  PlayCircle, 
  Film, 
  Eye, 
  Users, 
  TrendingUp, 
  RefreshCw,
  Activity,
  Server
} from 'lucide-react';
import { dashboardApi, DashboardStats } from '../services/dashboardApi';
import StatusIndicator, { healthNotes } from '../components/StatusIndicator';
import { audienceApi } from '../services/audienceApi';
import { EngagementDashboard } from '../types/audience';
import EngagementChart from '../components/audience/EngagementChart';
import LoadingSpinner from '../components/LoadingSpinner';

// Import the utility functions
import { getTimeAgo, formatDuration } from '../utils/dateUtils';

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [audienceData, setAudienceData] = useState<EngagementDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch both dashboard stats and audience data
      const [dashboardStats, engagementDashboard] = await Promise.all([
        dashboardApi.getDashboardStats(),
        audienceApi.getEngagementDashboard(7).catch(() => null) // Don't fail if no audience data
      ]);

      setStats(dashboardStats);
      setAudienceData(engagementDashboard);
    } catch (err) {
      setError('Failed to fetch dashboard data');
      console.error('Dashboard fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const formatFileSize = (bytes: number): string => {
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    if (bytes === 0) return '0 B';
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-600">{error}</p>
        <button 
          onClick={fetchDashboardData}
          className="mt-2 text-red-800 hover:text-red-900 underline"
        >
          Try again
        </button>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">No dashboard data available</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-gray-800">Dashboard</h1>
          <p className="text-gray-600">System overview and key metrics</p>
        </div>
        <button
          onClick={fetchDashboardData}
          className="flex items-center px-3 py-2 text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </button>
      </div>

      {/* Key Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Devices */}
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
              <Monitor className="w-5 h-5 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Devices</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.totalDevices}</p>
              <p className="text-sm text-gray-500">
                {stats.activeDevices} active
              </p>
            </div>
          </div>
        </div>

        {/* Playlists */}
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
              <PlayCircle className="w-5 h-5 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Playlists</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.totalPlaylists}</p>
              <p className="text-sm text-gray-500">
                {stats.activePlaylists} active
              </p>
            </div>
          </div>
        </div>

        {/* Assets */}
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
              <Film className="w-5 h-5 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Assets</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.totalAssets}</p>
              <p className="text-sm text-gray-500">
                {formatFileSize(stats.totalStorage)} total
              </p>
            </div>
          </div>
        </div>

        {/* System Health - Online Rate */}
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
              <Activity className="w-5 h-5 text-orange-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Online Rate</p>
              <p className="text-2xl font-semibold text-gray-900">
                {stats.totalDevices > 0
                  ? Math.round((stats.onlineDevices / stats.totalDevices) * 100)
                  : 0}%
              </p>
              <p className="text-sm text-gray-500">System Health</p>
            </div>
          </div>
        </div>
      </div>

      {/* Engagement Summary - Compact overview */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Engagement Summary</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="flex items-center justify-center w-12 h-12 bg-blue-100 rounded-lg mx-auto mb-2">
              <Users className="w-6 h-6 text-blue-600" />
            </div>
            <p className="text-2xl font-semibold text-gray-900">
              {audienceData?.totalViewers?.toLocaleString() || '0'}
            </p>
            <p className="text-sm text-gray-600">Total Faces</p>
          </div>
          
          <div className="text-center">
            <div className="flex items-center justify-center w-12 h-12 bg-green-100 rounded-lg mx-auto mb-2">
              <Eye className="w-6 h-6 text-green-600" />
            </div>
            <p className="text-2xl font-semibold text-gray-900">
             {audienceData?.topAssets && audienceData.topAssets.length > 0
              ? ((audienceData.topAssets.reduce((sum, asset) => sum + Math.min(Number(asset.averageAttentionScore), 1), 0) / 
                audienceData.topAssets.length) * 100).toFixed(1)
              : '0.0'}%
            </p>
            <p className="text-sm text-gray-600">Avg. Attention Score</p>
          </div>
          
          <div className="text-center">
            <div className="flex items-center justify-center w-12 h-12 bg-purple-100 rounded-lg mx-auto mb-2">
              <TrendingUp className="w-6 h-6 text-purple-600" />
            </div>
            <p className="text-lg font-medium text-gray-900 truncate">
              {audienceData?.topAssets && audienceData.topAssets.length > 0 
                ? audienceData.topAssets[0].assetName
                : 'No data yet'}
            </p>
            <p className="text-sm text-gray-600">Top Performing Asset</p>
          </div>
        </div>

        {(!audienceData || audienceData.totalViewers === 0) && (
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-xs text-blue-700">
              <strong>Camera Setup Required:</strong> Engagement data will populate once cameras are installed and configured with your displays.
            </p>
          </div>
        )}
      </div>

      {/* Device Status Table - The excellent table from the old dashboard */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center">
            <Server className="w-5 h-5 text-gray-600 mr-2" />
            <h2 className="text-lg font-semibold text-gray-800">Device Status</h2>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Device Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Location
                </th>
                {/* Two status columns, because a player fails in two unrelated
                    ways and one column had to pick a story to tell. The Pi can be
                    perfectly well while Pinage is stopped - which used to look
                    exactly like a device somebody had unplugged. */}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Pi Health
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Pinage
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  System Metrics
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Sync
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {stats.deviceStatuses.map((device) => (
                <tr key={device.deviceId} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="font-medium text-gray-900">
                      {device.deviceName}
                    </div>
                    {device.currentPlaylist && (
                      <div className="text-xs text-gray-500">
                        Playing: {device.currentPlaylist}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-600">
                    {device.location}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <StatusIndicator status={device.healthStatus} reasons={device.healthReasons} notes={healthNotes(device)} />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <StatusIndicator status={device.pinageStatus} reasons={device.pinageReasons} />
                    {device.updatePending && (
                      <div className="text-xs text-blue-600 mt-1">Update staged</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {device.metrics ? (
                      <div className="text-sm text-gray-500 space-y-1">
                        {device.metrics.cpuUsage !== undefined && (
                          <div className="flex items-center">
                            <span className="w-12 text-xs">CPU:</span>
                            <span className={`font-medium ${
                              device.metrics.cpuUsage > 80 ? 'text-red-600' : 
                              device.metrics.cpuUsage > 60 ? 'text-yellow-600' : 'text-green-600'
                            }`}>
                              {device.metrics.cpuUsage.toFixed(1)}%
                            </span>
                          </div>
                        )}
                        {device.metrics.memoryUsage !== undefined && (
                          <div className="flex items-center">
                            <span className="w-12 text-xs">RAM:</span>
                            <span className={`font-medium ${
                              device.metrics.memoryUsage > 80 ? 'text-red-600' : 
                              device.metrics.memoryUsage > 60 ? 'text-yellow-600' : 'text-green-600'
                            }`}>
                              {device.metrics.memoryUsage.toFixed(1)}%
                            </span>
                          </div>
                        )}
                        {device.metrics.temperature !== undefined && (
                          <div className="flex items-center">
                            <span className="w-12 text-xs">Temp:</span>
                            <span className={`font-medium ${
                              device.metrics.temperature > 80 ? 'text-red-600' : 
                              device.metrics.temperature > 70 ? 'text-yellow-600' : 'text-green-600'
                            }`}>
                              {device.metrics.temperature}°C
                            </span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="text-gray-400 text-sm">No data</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {getTimeAgo(device.lastSync) || 'Never'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {stats.deviceStatuses.length === 0 && (
            <div className="text-center py-8">
              <p className="text-gray-500">No devices found</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;